import { useCallback, useRef } from 'react';
import type { KeyboardEvent, ReactNode } from 'react';
import { cn } from './cn';

/**
 * Large serif tab nav. The page title IS the navigation: each tab is set
 * in the display serif at 22px, muted-by-default, fully-toned + weight-
 * bumped when selected.
 *
 * Headless on purpose — caller passes the items array and an `activeId`.
 * Click handler is per-item so consumers wire it to react-router or a
 * controlled state machine.
 *
 * Keyboard pattern: WAI-ARIA tabs (APG 3.21). Roving tabindex — only the
 * active tab is in the tab stop. ArrowLeft/Right cycle through enabled tabs
 * (wrapping); Home/End jump to first/last enabled tab. Disabled tabs are
 * skipped. Motion: transition-colors only, no transform animation, so the
 * global `prefers-reduced-motion` block in tokens.css covers this component
 * without additional `motion-reduce:` classes.
 */
export interface TabNavItem {
  id: string;
  label: string;
  /** Optional adornment after the label (e.g., a count chip). */
  trailing?: ReactNode;
  disabled?: boolean;
}

export interface TabNavProps {
  items: TabNavItem[];
  activeId: string;
  onChange: (id: string) => void;
  className?: string;
  /** ARIA label for the tablist landmark. */
  ariaLabel?: string;
}

export function TabNav({
  items,
  activeId,
  onChange,
  className,
  ariaLabel = 'Primary navigation',
}: TabNavProps) {
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>, currentIndex: number) => {
      const enabledIndices = items.reduce<number[]>((acc, item, i) => {
        if (!item.disabled) acc.push(i);
        return acc;
      }, []);

      if (enabledIndices.length === 0) return;

      const posInEnabled = enabledIndices.indexOf(currentIndex);
      if (posInEnabled === -1) return;

      let nextPos: number;
      switch (e.key) {
        case 'ArrowRight':
          nextPos = (posInEnabled + 1) % enabledIndices.length;
          break;
        case 'ArrowLeft':
          nextPos = (posInEnabled - 1 + enabledIndices.length) % enabledIndices.length;
          break;
        case 'Home':
          nextPos = 0;
          break;
        case 'End':
          nextPos = enabledIndices.length - 1;
          break;
        default:
          return;
      }

      e.preventDefault();
      const nextIndex = enabledIndices[nextPos];
      if (nextIndex === undefined) return;
      const nextItem = items[nextIndex];
      if (!nextItem) return;
      tabRefs.current[nextIndex]?.focus();
      onChange(nextItem.id);
    },
    [items, onChange],
  );

  return (
    <div role="tablist" aria-label={ariaLabel} className={cn('flex items-end gap-8', className)}>
      {items.map((item, index) => {
        const isActive = item.id === activeId;
        return (
          <button
            key={item.id}
            id={`tab-${item.id}`}
            ref={(el) => {
              tabRefs.current[index] = el;
            }}
            type="button"
            role="tab"
            aria-selected={isActive}
            aria-controls={`tabpanel-${item.id}`}
            tabIndex={isActive ? 0 : -1}
            disabled={item.disabled}
            onClick={() => onChange(item.id)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            className={cn(
              // min-h-[28px] keeps the click target at WCAG 2.5.5 minimum (24x24) even when
              // the label wraps or shrinks. Hit area is generous enough for touch.
              'inline-flex min-h-[28px] items-center gap-2 border-b-2 border-transparent pb-1 font-display text-display-md leading-none tracking-tight transition-colors duration-200 ease-out focus-visible:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring',
              isActive ? 'font-medium text-fg' : 'text-fg-3 hover:text-fg-2',
              item.disabled && 'cursor-not-allowed opacity-50',
            )}
          >
            <span>{item.label}</span>
            {item.trailing ? <span aria-hidden>{item.trailing}</span> : null}
          </button>
        );
      })}
    </div>
  );
}
